import {skipToken} from '@reduxjs/toolkit/query';
import {isFeatureEnabled} from 'feature-flags';
import {DataStatus, Dialog, useLocalStorage} from 'platform/components';

import {useState} from 'react';

import {isNil, isNotNil} from 'ramda';

import {
  type GetAdvertisementPreviewApiResponse,
  useGetAdvertisementPreviewQuery,
  useGetAdvertisementPreviewPhotosQuery,
  useGetSalesPlatformsQuery,
  useGetVehicleLocalizedContentQuery,
  useGetVehicleQuery,
} from '@dms/api/sales';
import type {AdvertisingPlatformCodeEnum} from '@dms/api/shared';
import {useGetVehicleQuery as useGetVehicleV2Query} from '@dms/api/vehicle';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {vehiclesRoutes} from '@dms/routes';
import {MIN_LANGUAGES_FOR_MULTILANGUAGE, useVehicleCatalogue} from '@dms/shared';
import {VehiclePhotosRoutes} from '@dms/teas';

import {composePath, useNavigate, type RequiredTestIdProps} from 'shared/web';

import {useAdvertisementPreviewQueryArgs} from '../hooks/useAdvertisementPreviewQueryArgs';
import {AdvertisementPreviewBody} from './AdvertisementPreview/components/AdvertisementPreviewBody';
import {AdvertisementPreviewRefreshAction} from './AdvertisementPreview/components/AdvertisementPreviewRefreshAction';
import {AdvertisementPreviewSkeleton} from './AdvertisementPreview/components/AdvertisementPreviewSkeleton';
import {PREVIEW_PLATFORM_STORAGE_KEY} from './AdvertisementPreview/constants/PREVIEW_PLATFORM_STORAGE_KEY';

type PreviewAction = () => Promise<boolean>;

export interface AdvertisementPreviewDialogProps extends RequiredTestIdProps {
  isOpen: boolean;
  vehicleId: string;
  initialPlatformCode?: AdvertisingPlatformCodeEnum;
  inAdvertisement: boolean;
  hasPublishPermission: boolean;
  hasUpdatePermission: boolean;
  isPublishBlocked: boolean;
  onPublish: PreviewAction;
  onUpdate: PreviewAction;
  onClose: () => void;
}

export function AdvertisementPreviewDialog(props: AdvertisementPreviewDialogProps) {
  return (
    <Dialog
      isOpen={props.isOpen}
      onClose={props.onClose}
      title={i18n.t('page.advertisement.preview.title')}
      size="large"
      pagination={<AdvertisementPreviewRefreshAction vehicleId={props.vehicleId} />}
      withAdditionalFooter
      disableBodyPadding
      data-testid={props['data-testid']}
    >
      <AdvertisementPreviewDialogContent {...props} />
    </Dialog>
  );
}

function AdvertisementPreviewDialogContent(props: AdvertisementPreviewDialogProps) {
  const navigate = useNavigate();
  const [storedPlatform, setStoredPlatform] = useLocalStorage<string | undefined>(
    PREVIEW_PLATFORM_STORAGE_KEY,
    undefined
  );
  const [requestedPlatformCode, setRequestedPlatformCode] = useState<
    AdvertisingPlatformCodeEnum | undefined
  >(props.initialPlatformCode);
  const [requestedLanguage, setRequestedLanguage] = useState<string>();
  const [isSubmitting, setSubmitting] = useState(false);
  const [vehicleCatalogue, {isLoading: isVehicleCatalogueLoading}] = useVehicleCatalogue(null);

  const previewArgs = useAdvertisementPreviewQueryArgs(props.vehicleId, props.inAdvertisement);
  const preview = useGetAdvertisementPreviewQuery(
    previewArgs === skipToken ? skipToken : {...previewArgs, language: requestedLanguage}
  );
  const hasLanguageTabs =
    isFeatureEnabled(featureFlags.ADVERTISEMENT_MULTILANGUAGE_CONTENT) &&
    (preview.data?.languages.length ?? 0) >= MIN_LANGUAGES_FOR_MULTILANGUAGE;
  const localizedContentQuery = useGetVehicleLocalizedContentQuery(
    hasLanguageTabs ? {vehicleId: props.vehicleId} : skipToken
  );
  const salesPlatformsQuery = useGetSalesPlatformsQuery({vehicleId: props.vehicleId});
  const vehicleQuery = useGetVehicleQuery({vehicleId: props.vehicleId});
  // V1 supplies price/VAT; V2 supplies WLTP specs.
  const vehicleV2Query = useGetVehicleV2Query({vehicleId: props.vehicleId});
  const localizedContent = localizedContentQuery.data;
  const salesPlatforms = salesPlatformsQuery.data;

  const localizedContentByLanguage = Object.fromEntries(
    localizedContent?.map((item) => [item.language, item]) ?? []
  );
  const isLanguageSwitchFetch =
    preview.isFetching &&
    isNotNil(preview.data) &&
    !isRenderedLanguageRequested(preview.data, requestedLanguage);
  const isPreviewLoading = preview.isLoading || (preview.isFetching && !isLanguageSwitchFetch);
  const platformTitles = new Map(
    salesPlatforms?.map((platform) => [platform.platform.code, platform.platform.title]) ?? []
  );
  const preferredPlatformCode = requestedPlatformCode ?? storedPlatform;
  const activePlatformCode =
    preview.data?.platforms.find((platform) => platform.platformCode === preferredPlatformCode)
      ?.platformCode ?? preview.data?.platforms[0]?.platformCode;
  const photosQuery = useGetAdvertisementPreviewPhotosQuery(
    isNotNil(activePlatformCode)
      ? {vehicleId: props.vehicleId, platformCode: activePlatformCode}
      : skipToken
  );

  const handlePlatformChange = (platformCode: AdvertisingPlatformCodeEnum) => {
    setRequestedPlatformCode(platformCode);
    setStoredPlatform(platformCode);
  };

  const handlePhotosNavigation = () => {
    props.onClose();
    navigate(
      composePath(vehiclesRoutes.photos, {
        params: {id: props.vehicleId, tab: VehiclePhotosRoutes.vehiclePhotos},
      })
    );
  };
  const handleSubmit = () => {
    setSubmitting(true);
    const action = props.inAdvertisement ? props.onUpdate : props.onPublish;
    action().then((isSuccessful) => {
      if (isSuccessful) {
        props.onClose();
        return;
      }
      setSubmitting(false);
    });
  };

  if (preview.isError) {
    return <DataStatus isError minHeight={400} />;
  }

  if (
    isPreviewLoading ||
    isNil(preview.data) ||
    salesPlatformsQuery.isLoading ||
    isVehicleCatalogueLoading
  ) {
    return <AdvertisementPreviewSkeleton data-testid={`${props['data-testid']}-skeleton`} />;
  }

  return (
    <AdvertisementPreviewBody
      preview={{
        data: preview.data,
        photosQuery: {
          data: photosQuery.currentData,
          isLoading:
            photosQuery.isFetching || (photosQuery.isUninitialized && isNotNil(activePlatformCode)),
          isError: photosQuery.isError,
        },
        platformTitles,
        salesPlatforms,
        vehicleCatalogue,
        isVehicleCatalogueLoading,
        pricingVehicle: vehicleQuery.data,
        specificationsVehicle: vehicleV2Query.data,
      }}
      selection={{activePlatformCode, requestedLanguage, localizedContentByLanguage}}
      hasLanguageTabs={hasLanguageTabs}
      isLanguageSwitchFetch={isLanguageSwitchFetch}
      inAdvertisement={props.inAdvertisement}
      hasUpdatePermission={props.hasUpdatePermission}
      hasPublishPermission={props.hasPublishPermission}
      isPublishBlocked={props.isPublishBlocked}
      isSubmitting={isSubmitting}
      onSubmit={handleSubmit}
      onPlatformChange={handlePlatformChange}
      onLanguageChange={setRequestedLanguage}
      onAddPhotos={handlePhotosNavigation}
      onClose={props.onClose}
      vehicleId={props.vehicleId}
      data-testid={props['data-testid']}
    />
  );
}

function isRenderedLanguageRequested(
  data: GetAdvertisementPreviewApiResponse,
  requestedLanguage: string | undefined
) {
  if (isNotNil(requestedLanguage)) {
    return data.language === requestedLanguage;
  }

  return data.languages.some(
    (language) => language.language === data.language && language.isDefault
  );
}
